import React, { FC, useEffect, useState } from "react"
import Pagination from "../Pagination"
import styled from "@emotion/styled"
import { useTranslation } from "react-i18next"
import { SectionTitle } from "@/components/Content"
import useLever from "../utils/useLever"
import { mobileFirstDevice, containerSizes } from "@/utils/media-queries"
import { SearchBar } from "./SearchBar"
import { Visible } from "react-grid-system"
import { Filter } from "./Filter"
import { useDebouncedValue } from "@/components/hooks/useDebouncedValue"
import { useSearchJob } from "../utils/useSearchJob"
import { usePageChange } from "../utils/usePageChange"
import Flex from "@/components/Flex"
import Typography from "@/components/Typography"
import FieldWrapper from "./components/FieldWrapper"
import Select from "./components/Select"
import Box from "@/components/Box"
import { useIsPortableDevice } from "@/components/hooks/useIsPortableDevice"
import Spinner from "@/components/Spinner"
import { NoRTLProvider } from "@/components/Layout/RTLProvider"

const pageSize = 10

const Wrapper = styled.div`
  width: 100%;
  text-align: left;
  padding: 0 24px 465px;
  overflow-y: hidden;

  @media ${mobileFirstDevice.laptop} {
    width: ${containerSizes.laptop};
    margin: 0 auto 200px auto;
  }

  @media ${mobileFirstDevice.tablet} {
    padding: 0 56px 465px;
  }
`

const Header = styled(SectionTitle)`
  color: #000000;
  margin-bottom: 24px;
  font-size: 42px;
`

const PositionWrapper = styled.div`
  margin-top: 32px;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media ${mobileFirstDevice.laptop} {
    margin-top: 0;
  }

  @media ${mobileFirstDevice.tablet} {
    margin-top: 0;
  }
`

const Position = styled.div<{ color: string }>`
  position: relative;
  padding-right: 20px;
  border-top: 1px solid #9ea1a2;
  padding-top: 16px;
  padding-bottom: 16px;

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    right: 0;
    width: 20px;
    height: 20px;
    border-right: 2px solid ${props => props.color};
    border-bottom: 2px solid ${props => props.color};
    transform: rotate(-45deg) translateY(-50%);
  }

  > div:nth-child(1) {
    font-weight: 600;
    font-size: 18px;
    line-height: 100%;
    letter-spacing: 0.02em;
    color: #000000;
    margin-bottom: 12px;
    max-width: 85%;
    @media ${mobileFirstDevice.tablet} {
      font-size: 24px;
    }
  }
`

const JoinTeamSection: FC = () => {
  const { t } = useTranslation("careers")
  const jobData = useLever()
  const { jobs, jobMapByLocation, jobMapByTeam, isLoading } = jobData
  const { page, handleChangePage } = usePageChange(1)
  const [teamsFilter, setTeamsFilter] = useState(["All Teams"])
  const [locFilter, setLocFilter] = useState(["All Location"])
  const [searchStr, setSearchStr] = useState("")
  const [isHover, setIsHover] = useState("")
  const debouncedSearchStr = useDebouncedValue(searchStr, 300)
  const isMobileOrTablet = useIsPortableDevice()
  const toRender = useSearchJob(
    jobData,
    debouncedSearchStr,
    locFilter,
    teamsFilter
  )

  useEffect(() => {
    handleChangePage(1)
    if (!teamsFilter.length) {
      setTeamsFilter(["All Teams"])
    }
    if (!locFilter.length) {
      setLocFilter(["All Location"])
    }
  }, [teamsFilter, locFilter])

  return (
    <Wrapper id="jobs">
      <Header>{t("join.title")}</Header>
      <Flex flexWrap="nowrap" position="relative" customCss={{ gap: 3 }}>
        <Box width={["100%", "39%", "39%"]}>
          <FieldWrapper label={t("join.search") as string}>
            <SearchBar
              value={searchStr}
              onChange={value => {
                setSearchStr(value)
                handleChangePage(1)
              }}
              px={2}
              py={"14px"}
              width="100%"
              mb={"26px"}
              ariaLabel={t("join.search")}
            ></SearchBar>
          </FieldWrapper>
        </Box>
        <Visible md lg xl xxl>
          <Box width={["100%", "28%", "28%"]}>
            <FieldWrapper label={t("join.teams") as string}>
              <Select
                width={"100%"}
                label={t("join.teams")}
                data={jobMapByTeam}
                filterArray={teamsFilter}
                setFilter={setTeamsFilter}
                allField={"All Teams"}
              />
            </FieldWrapper>
          </Box>
          <Box width={["100%", "28%", "28%"]}>
            <FieldWrapper label={t("join.location") as string}>
              <Select
                width={"100%"}
                label={t("join.location")}
                data={jobMapByLocation}
                filterArray={locFilter}
                setFilter={setLocFilter}
                allField={"All Location"}
              />
            </FieldWrapper>
          </Box>
        </Visible>
      </Flex>
      {/* <Label>{t("join.location")}</Label> */}
      <Visible xs sm>
        <Filter
          jobMapByTeam={jobMapByTeam}
          jobMapByLocation={jobMapByLocation}
          teamsFilter={teamsFilter}
          setTeamsFilter={setTeamsFilter}
          locFilter={locFilter}
          setLocFilter={setLocFilter}
          resultsCount={toRender.length}
        ></Filter>
      </Visible>
      <PositionWrapper>
        <div>
          {isLoading ? (
            <Flex
              width={"100%"}
              mt={8}
              height={147}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Spinner />
            </Flex>
          ) : toRender.length !== 0 ? (
            toRender
              .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
              .map((jobID, index, { length }) => {
                const job = jobs[jobID]
                return (
                  <a
                    href={job.hostedUrl}
                    key={jobID}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Position
                      style={
                        length - 1 === index
                          ? { borderBottom: "1px solid #9EA1A2" }
                          : { borderBottom: "none" }
                      }
                      onMouseOver={() => setIsHover(jobID)}
                      onMouseOut={() => setIsHover("")}
                      onTouchStart={() => setIsHover(jobID)}
                      color={isHover === jobID ? "#0058aa" : "#9EA1A2"}
                    >
                      <div>{job.text}</div>
                      <Flex>
                        <Typography
                          variant="careerJobSubtitle"
                          color="primaryBrightBlueLightBg"
                          pr={["12px", 2, 2]}
                          borderRight={"1px solid #9EA1A2"}
                        >
                          {job.categories.department}
                        </Typography>
                        <Typography
                          variant="careerJobSubtitle"
                          color="primaryBrightBlueLightBg"
                          pl={["12px", 2, 2]}
                        >
                          {job.categories.location}
                        </Typography>
                      </Flex>
                      {!isMobileOrTablet && isHover === jobID && (
                        <Box
                          position={"absolute"}
                          top="43%"
                          right="3%"
                          zIndex={-1}
                        >
                          <Typography
                            variant="primaryCta"
                            color="primaryBrightBlueLightBg"
                          >
                            {"Apply Now"}
                          </Typography>
                        </Box>
                      )}
                    </Position>
                  </a>
                )
              })
          ) : (
            <Typography
              display={["none", "block", "block"]}
              variant="p"
              fontSize={"24px"}
              fontWeight={600}
              lineHeight={"145%"}
              color={"#061121"}
            >
              {"No results"}
            </Typography>
          )}
        </div>
        <NoRTLProvider>
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            onChange={handleChangePage}
            total={toRender.length}
          />
        </NoRTLProvider>
      </PositionWrapper>
    </Wrapper>
  )
}

export default JoinTeamSection
